import {
  MAX_RECORD_BYTES,
  ShareRefusal,
  categoryOfDocument,
  deliveredRecordId,
  documentsUnder,
  fhir,
  sharedRecordId,
  withStoreLock,
  type ConsentTerms,
  type DeliveryPayload,
  type DispenseNoticePayload,
  type HeldRecord,
  type PublicAccount,
  type VaultKey,
} from '@curentia/shared';
import { VaultRecordStore } from './records';
import type { LocalStore } from './store';

const byteLength = (value: unknown) => new TextEncoder().encode(JSON.stringify(value)).length;

/** The Patient a document is about: its Composition's subject. */
function subjectOf(bundle: fhir.Bundle): unknown {
  const composition = bundle.entry?.[0]?.resource;
  const reference = composition?.resourceType === 'Composition' ? composition.subject.reference : undefined;
  return bundle.entry?.find((entry) => entry.fullUrl === reference)?.resource;
}

/**
 * A patient's own records on their phone: copies of the prescriptions and
 * readings their doctors send them. Patients do not write records
 * themselves; their doctors do. The records live in the encrypted vault under
 * mine and go into the backup file like everything else. Copies a practice
 * sent are not passed on to other practices: each record's source shares it.
 */
export class PersonalRecords {
  private readonly store: VaultRecordStore;
  private selfId: string | null = null;

  constructor(
    db: LocalStore,
    key: VaultKey,
    private readonly account: Pick<PublicAccount, 'id' | 'name' | 'mobile'>,
  ) {
    this.store = new VaultRecordStore(db, key, 'mine');
  }

  /** The patient's own ID in this store, the same on every device that restores the backup. */
  private async patientId(): Promise<string> {
    this.selfId ??= await sharedRecordId('self', this.account.id);
    return this.selfId;
  }

  /** Every document on the phone, newest first by its own date. */
  async documents(): Promise<HeldRecord[]> {
    const entries = (await this.store.since(0)).filter((entry) => entry.kind === 'document');
    const records: HeldRecord[] = [];
    for (const entry of entries) {
      const record = await this.store.get(entry.id);
      if (record) records.push(record);
    }
    return records.sort((a, b) => (b.date ?? b.createdAt).localeCompare(a.date ?? a.createdAt));
  }

  async get(id: string): Promise<HeldRecord | null> {
    const record = await this.store.get(id);
    return record?.kind === 'document' ? record : null;
  }

  /**
   * Keeps a document a linked practice sent: a prescription or a copy of a
   * record, such as readings taken at the clinic. The caller has checked the
   * sender and the practice. This checks the document: its structure, size,
   * and that it is about this patient's own account. A resend of a version
   * already kept changes nothing.
   */
  async take(input: { payload: DeliveryPayload; practiceName: string; senderId: string; now: Date }): Promise<{ record: HeldRecord; added: boolean }> {
    const { practice, record } = input.payload;
    const [issue] = fhir.checkDocument(record.resource, record.hiType);
    if (issue) throw new ShareRefusal(`The document is not valid: ${issue.path} ${issue.message}`.slice(0, 300));
    if (byteLength(record.resource) > MAX_RECORD_BYTES) throw new ShareRefusal('The document is too large');
    const bundle = record.resource as unknown as fhir.Bundle;
    if (fhir.patientAccountId(subjectOf(bundle)) !== this.account.id) throw new ShareRefusal('The document is about someone else');
    const id = await deliveredRecordId(practice.id, record.id);
    const at = input.now.toISOString();
    const patientId = await this.patientId();
    return withStoreLock(this.store, async () => {
      const existing = await this.store.get(id);
      if (existing && (existing.source?.version ?? 0) >= record.version) return { record: existing, added: false };
      const saved = await this.store.save({
        id,
        patientId,
        kind: 'document',
        category: categoryOfDocument(record.hiType),
        hiType: record.hiType,
        version: (existing?.version ?? 0) + 1,
        resource: record.resource,
        date: fhir.documentDateOf(bundle) ?? at,
        source: { from: 'practice', practiceId: practice.id, practiceName: input.practiceName.slice(0, 120), recordId: record.id, version: record.version, receivedAt: at },
        createdAt: existing?.createdAt ?? at,
        createdBy: existing?.createdBy ?? input.senderId,
        updatedAt: at,
        updatedBy: input.senderId,
      });
      return { record: saved, added: existing === null };
    });
  }

  /**
   * Keeps a pharmacy's notice that it dispensed against one of the patient's
   * prescriptions. The caller has checked the sender and the doctor's
   * signature on the prescription. This checks that the prescription is about
   * this patient and that the dispensing record answers it, then files the
   * record beside the phone's copy of the prescription. A phone without that
   * copy keeps the signed prescription from the notice too.
   */
  async takeDispense(input: { payload: DispenseNoticePayload; pharmacyName: string; senderId: string; now: Date }): Promise<{ record: HeldRecord; added: boolean }> {
    const { prescription, dispense, dispensedBy } = input.payload;
    const [issue] = fhir.checkDocument(prescription.resource, 'Prescription');
    if (issue) throw new ShareRefusal(`The prescription is not valid: ${issue.path} ${issue.message}`.slice(0, 300));
    const bundle = prescription.resource as unknown as fhir.Bundle;
    if (fhir.patientAccountId(subjectOf(bundle)) !== this.account.id) throw new ShareRefusal('The prescription is about someone else');
    if (byteLength(dispense.resource) > MAX_RECORD_BYTES) throw new ShareRefusal('The dispensing record is too large');
    let matches = false;
    try {
      matches = fhir.dispenseMatchesPrescription(fhir.readDispenseRecord(dispense.resource as unknown as fhir.Bundle), fhir.readPrescription(bundle));
    } catch {
      matches = false;
    }
    if (!matches) throw new ShareRefusal('The dispensing record does not answer the prescription');
    const copyId = await deliveredRecordId(prescription.practiceId, prescription.recordId);
    const id = await deliveredRecordId(dispensedBy, dispense.id);
    const at = input.now.toISOString();
    const patientId = await this.patientId();
    return withStoreLock(this.store, async () => {
      if (!(await this.store.get(copyId))) {
        const practiceName = fhir.readPrescription(bundle).practice?.name ?? 'Your doctor’s practice';
        await this.store.save({
          id: copyId,
          patientId,
          kind: 'document',
          category: categoryOfDocument('Prescription'),
          hiType: 'Prescription',
          version: 1,
          resource: prescription.resource,
          date: fhir.documentDateOf(bundle) ?? at,
          source: { from: 'practice', practiceId: prescription.practiceId, practiceName: practiceName.slice(0, 120), recordId: prescription.recordId, version: 1, receivedAt: at },
          createdAt: at,
          createdBy: input.senderId,
          updatedAt: at,
          updatedBy: input.senderId,
        });
      }
      const existing = await this.store.get(id);
      if (existing && (existing.source?.version ?? 0) >= dispense.version) return { record: existing, added: false };
      const saved = await this.store.save({
        id,
        patientId,
        kind: 'dispense',
        category: 'dispensing',
        version: (existing?.version ?? 0) + 1,
        resource: dispense.resource,
        date: fhir.documentDateOf(dispense.resource) ?? at,
        prescriptionId: copyId,
        source: { from: 'practice', practiceId: dispensedBy, practiceName: input.pharmacyName.slice(0, 120), recordId: dispense.id, version: dispense.version, receivedAt: at },
        createdAt: existing?.createdAt ?? at,
        createdBy: existing?.createdBy ?? input.senderId,
        updatedAt: at,
        updatedBy: input.senderId,
      });
      return { record: saved, added: existing === null };
    });
  }

  /** The practices that dispensed for this patient, newest first, by the ID and name their notices gave. */
  async dispensers(): Promise<{ id: string; name: string }[]> {
    const entries = (await this.store.since(0)).filter((entry) => entry.kind === 'dispense' && entry.source?.practiceId);
    const found = new Map<string, { id: string; name: string }>();
    for (const entry of [...entries].reverse()) {
      const id = entry.source?.practiceId;
      if (id && !found.has(id)) found.set(id, { id, name: entry.source?.practiceName ?? 'A pharmacy' });
    }
    return [...found.values()];
  }

  /** What pharmacies dispensed against one of the phone's prescriptions, oldest first. */
  async dispensesFor(prescriptionId: string): Promise<HeldRecord[]> {
    const entries = (await this.store.since(0)).filter((entry) => entry.kind === 'dispense' && entry.prescriptionId === prescriptionId);
    const records: HeldRecord[] = [];
    for (const entry of entries) {
      const record = await this.store.get(entry.id);
      if (record) records.push(record);
    }
    return records.sort((a, b) => (a.date ?? a.createdAt).localeCompare(b.date ?? b.createdAt));
  }

  /**
   * The documents a consent covers that this phone itself made, to send when
   * the patient approves. Copies from practices are left out, since each
   * practice shares its own when the patient names it as a source.
   */
  async under(terms: ConsentTerms): Promise<HeldRecord[]> {
    return documentsUnder(this.store, [await this.patientId()], terms);
  }
}
